import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Mail, MapPin, Phone, Send } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API, COMMODITIES, COMMODITIES_FR, PAYMENT_INSTRUMENTS, INCOTERMS } from "@/lib/constants";
import { useLanguage } from "@/i18n/LanguageContext";
import Reveal from "@/components/Reveal";

const INITIAL = {
  company_name: "",
  corporate_address: "",
  rep_name: "",
  rep_title: "",
  email: "",
  phone: "",
  commodity: "",
  quantity_mt: "",
  payment_instrument: "",
  incoterm: "",
  destination_port: "",
  message: "",
};

const Field = ({ label, children }) => (
  <div>
    <label className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-slate-400">
      {label}
    </label>
    <div className="mt-2">{children}</div>
  </div>
);

const inputCls =
  "w-full border border-white/15 bg-[#0d223f] px-4 py-3 text-sm text-white outline-none transition-colors focus:border-[#4a6cf7] placeholder:text-slate-600";

const Contact = () => {
  const [searchParams] = useSearchParams();
  const preselect = searchParams.get("commodity") || "";
  const { t, lang } = useLanguage();
  const commodityOptions = lang === "fr" ? COMMODITIES_FR : COMMODITIES;
  const [form, setForm] = useState({ ...INITIAL, commodity: preselect });
  const [sending, setSending] = useState(false);
  const [reference, setReference] = useState(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/inquiries`, {
        inquiry_type: "rfq",
        ...Object.fromEntries(Object.entries(form).map(([k, v]) => [k, v || null])),
        company_name: form.company_name,
        email: form.email,
      });
      setReference(data.reference);
      toast.success(`${t("contact.toastOk")} ${data.reference}`);
    } catch (err) {
      toast.error(t("contact.toastErr"));
    } finally {
      setSending(false);
    }
  };

  return (
    <main data-testid="contact-page" className="pt-32">
      <section className="mx-auto max-w-7xl px-5 pb-24 lg:px-8">
        <Reveal>
          <p className="font-mono2 text-xs uppercase tracking-[0.35em] text-[#9db4ff]">
            {t("contact.kicker")}
          </p>
          <h1 className="font-display mt-3 text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl">
            {t("contact.titleA")} <span className="gold-text">{t("contact.titleGold")}</span>
          </h1>
          <p className="mt-5 max-w-2xl leading-relaxed text-slate-400">{t("contact.intro")}</p>
        </Reveal>

        <div className="mt-14 grid gap-10 lg:grid-cols-3">
          {/* CONTACT INFO */}
          <Reveal className="lg:col-span-1">
            <div className="space-y-8 border border-white/10 bg-[#0d223f] p-8">
              <div>
                <p className="font-mono2 flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[#9db4ff]">
                  <MapPin className="h-4 w-4" /> {t("contact.office")}
                </p>
                <p className="mt-3 text-sm leading-relaxed text-slate-300" data-testid="contact-address">
                  Ore Source Mining Sarlu
                  <br />
                  5967 AV Kasai, Q/Makutano C/Lubumbashi
                  <br />
                  Katanga — Lubumbashi, DR Congo
                </p>
              </div>
              <div>
                <p className="font-mono2 flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[#9db4ff]">
                  <Mail className="h-4 w-4" /> {t("contact.trade")}
                </p>
                <p className="mt-3 text-sm leading-relaxed text-slate-300" data-testid="contact-emails">
                  info@oresourcemining.com
                  <br />
                  sales@oresourcemining.com
                </p>
              </div>
              <div>
                <p className="font-mono2 flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[#9db4ff]">
                  <Phone className="h-4 w-4" /> {t("contact.line")}
                </p>
                <p className="mt-3 text-sm text-slate-300" data-testid="contact-phone">
                  +243 999 004 146
                </p>
              </div>
              <div className="border-t border-white/10 pt-6">
                <p className="text-xs leading-relaxed text-slate-500">{t("contact.note")}</p>
              </div>
            </div>
          </Reveal>

          {/* RFQ FORM */}
          <Reveal delay={0.12} className="lg:col-span-2">
            {reference ? (
              <div
                data-testid="rfq-success-message"
                className="flex h-full flex-col items-start justify-center border border-[#4a6cf7]/50 bg-[#0d223f] p-10"
              >
                <p className="font-mono2 text-xs uppercase tracking-[0.3em] text-[#9db4ff]">
                  {t("contact.successKicker")}
                </p>
                <p className="font-display mt-4 text-3xl font-bold text-white sm:text-4xl">
                  {reference}
                </p>
                <p className="mt-5 max-w-md text-sm leading-relaxed text-slate-400">
                  {t("contact.successBody")}
                </p>
                <button
                  data-testid="rfq-new-button"
                  onClick={() => {
                    setReference(null);
                    setForm(INITIAL);
                  }}
                  className="mt-8 border border-[#4a6cf7]/60 px-6 py-3 font-display text-xs font-semibold uppercase tracking-[0.2em] text-[#9db4ff] transition-colors hover:bg-[#4a6cf7] hover:text-white"
                >
                  {t("contact.newRfq")}
                </button>
              </div>
            ) : (
              <form
                onSubmit={submit}
                data-testid="rfq-form"
                className="border border-[#4a6cf7]/25 bg-[#0d223f] p-8"
              >
                <div className="grid gap-5 sm:grid-cols-2">
                  <Field label={t("contact.f.company")}>
                    <input required data-testid="rfq-company-input" value={form.company_name} onChange={set("company_name")} className={inputCls} placeholder={t("contact.f.companyPh")} />
                  </Field>
                  <Field label={t("contact.f.address")}>
                    <input data-testid="rfq-address-input" value={form.corporate_address} onChange={set("corporate_address")} className={inputCls} placeholder={t("contact.f.addressPh")} />
                  </Field>
                  <Field label={t("contact.f.repName")}>
                    <input data-testid="rfq-rep-name-input" value={form.rep_name} onChange={set("rep_name")} className={inputCls} placeholder={t("contact.f.repNamePh")} />
                  </Field>
                  <Field label={t("contact.f.repTitle")}>
                    <input data-testid="rfq-rep-title-input" value={form.rep_title} onChange={set("rep_title")} className={inputCls} placeholder={t("contact.f.repTitlePh")} />
                  </Field>
                  <Field label={t("contact.f.email")}>
                    <input required type="email" data-testid="rfq-email-input" value={form.email} onChange={set("email")} className={inputCls} placeholder={t("contact.f.emailPh")} />
                  </Field>
                  <Field label={t("contact.f.phone")}>
                    <input data-testid="rfq-phone-input" value={form.phone} onChange={set("phone")} className={inputCls} placeholder={t("contact.f.phonePh")} />
                  </Field>
                  <Field label={t("contact.f.commodity")}>
                    <select required data-testid="rfq-commodity-select" value={form.commodity} onChange={set("commodity")} className={inputCls}>
                      <option value="">{t("contact.f.commodityPh")}</option>
                      {commodityOptions.map((c, i) => (
                        <option key={COMMODITIES[i]} value={COMMODITIES[i]}>{c}</option>
                      ))}
                    </select>
                  </Field>
                  <Field label={t("contact.f.quantity")}>
                    <input data-testid="rfq-quantity-input" value={form.quantity_mt} onChange={set("quantity_mt")} className={inputCls} placeholder={t("contact.f.quantityPh")} />
                  </Field>
                  <Field label={t("contact.f.payment")}>
                    <select data-testid="rfq-payment-select" value={form.payment_instrument} onChange={set("payment_instrument")} className={inputCls}>
                      <option value="">{t("contact.f.paymentPh")}</option>
                      {PAYMENT_INSTRUMENTS.map((p) => (
                        <option key={p} value={p}>{p}</option>
                      ))}
                    </select>
                  </Field>
                  <Field label={t("contact.f.incoterm")}>
                    <select data-testid="rfq-incoterm-select" value={form.incoterm} onChange={set("incoterm")} className={inputCls}>
                      <option value="">{t("contact.f.incotermPh")}</option>
                      {INCOTERMS.map((i) => (
                        <option key={i} value={i}>{i}</option>
                      ))}
                    </select>
                  </Field>
                  <Field label={t("contact.f.port")}>
                    <input data-testid="rfq-port-input" value={form.destination_port} onChange={set("destination_port")} className={inputCls} placeholder={t("contact.f.portPh")} />
                  </Field>
                  <div className="sm:col-span-2">
                    <Field label={t("contact.f.message")}>
                      <textarea rows={4} data-testid="rfq-message-input" value={form.message} onChange={set("message")} className={`${inputCls} resize-none`} placeholder={t("contact.f.messagePh")} />
                    </Field>
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="rfq-submit-button"
                  className="mt-7 flex w-full items-center justify-center gap-3 bg-gradient-to-r from-[#4a6cf7] to-[#2f49c6] px-6 py-4 font-display text-sm font-bold uppercase tracking-[0.2em] text-white transition-transform duration-300 hover:scale-[1.01] active:scale-[0.98] disabled:opacity-60"
                >
                  <Send className="h-4 w-4" />
                  {sending ? t("contact.f.sending") : t("contact.f.submit")}
                </button>
              </form>
            )}
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default Contact;
